/**
 * Spec 054, step 3b: the client's accesses in the app. «Доступы» on the client's screen — what the
 * client gave through the safe link, its secrets by name and who took a value, by which order and
 * when; the secret's owner deletes it. «Ссылка для доступов» on the order — a new link to copy and
 * send any way: the client writes the accesses down on the tasker's page, and they go straight into
 * the secrets store, never through a chat.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ClientAccesses, type AccessLinkOut, type ClientAccessOut } from "@/api/orders";
import { Secrets } from "@/api/resources";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { EmptyNote } from "@/kit";
import { accessLine, useLine } from "@/lib/clientAccess";
import { clock } from "@/lib/lead";

const accessesKey = (clientId: string) => ["client-accesses", clientId] as const;

export function ClientAccessesBlock({ clientId }: { clientId: string }) {
  const accesses = useQuery({ queryKey: accessesKey(clientId), queryFn: () => ClientAccesses.list(clientId) });
  if (accesses.isLoading) return <SkeletonList count={1} />;
  if (!accesses.data) {
    return <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void accesses.refetch()}>Повторить</Button>}>Доступы не загрузились.</EmptyNote>;
  }
  if (!accesses.data.length) {
    return (
      <p className="text-meta text-p-text-muted">
        Доступов пока нет. Клиент передаёт их сам на странице таскера: ссылку для него дай в заказе — «Ссылка для доступов».
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {accesses.data.map((a) => <AccessCard key={a.resource_id} access={a} clientId={clientId} />)}
    </div>
  );
}

function AccessCard({ access: a, clientId }: { access: ClientAccessOut; clientId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const destroy = useMutation({
    mutationFn: (secret: { id: string; version: number }) => Secrets.destroy(secret.id, secret.version),
    onSuccess: () => {
      toast.success("Доступ удалён: значения больше нет, журнал остался");
      void qc.invalidateQueries({ queryKey: accessesKey(clientId) });
    },
    onError: (error: Error) => toast.error(error.message),
  });
  return (
    <Card className="flex flex-col gap-1.5">
      <p className="text-body font-semibold text-p-text">{a.name}</p>
      <p className="text-meta text-p-text-muted">{accessLine(a)}</p>
      {a.comment && <p className="whitespace-pre-wrap text-meta text-p-text">{a.comment}</p>}
      {a.secrets.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-meta text-p-text">
            {s.name}{s.destroyed ? " — удалён" : ""} · {clock(s.created_at)}
          </span>
          {s.mine && !s.destroyed && (
            <Button size="compact" variant="ghost" loading={destroy.isPending}
              onClick={() => { if (window.confirm(`Удалить «${a.name}: ${s.name}»? Значение исчезнет, журнал останется.`)) destroy.mutate(s); }}>
              Удалить
            </Button>
          )}
        </div>
      ))}
      {a.used.length > 0 && (
        <details className="space-y-1">
          <summary className="min-h-11 cursor-pointer py-2 text-meta font-semibold text-p-text">Кто брал · {a.used.length}</summary>
          <ul className="space-y-0.5 text-meta text-p-text-muted">
            {a.used.map((u, i) => <li key={i}>{useLine(u)}</li>)}
          </ul>
        </details>
      )}
    </Card>
  );
}

/** A new safe link of the order: what we ask for, then the link to copy — it is shown once. */
export function AccessLinkSheet({ orderId, label, open, onClose }: { orderId: string; label: string; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [what, setWhat] = useState("");
  const [link, setLink] = useState<AccessLinkOut | null>(null);
  const make = useMutation({
    mutationFn: () => ClientAccesses.link(orderId, what.trim() || null),
    onSuccess: setLink,
    onError: (error: Error) => toast.error(error.message),
  });
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не получилось скопировать — выдели ссылку и скопируй вручную");
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title={`Ссылка для доступов · ${label}`}
      footer={link
        ? <Button block onClick={() => void copy()}>Скопировать ссылку</Button>
        : <Button block loading={make.isPending} loadingText="Создаём…" onClick={() => make.mutate()}>Создать ссылку</Button>}>
      <div className="flex flex-col gap-3">
        {link ? (
          <>
            <p className="select-all break-all rounded-xl border border-p-border px-3 py-2 font-mono text-[13px] text-p-text">{link.url}</p>
            <p className="text-meta text-p-text-muted">{link.rule} Действует до {clock(link.expires_at)}.</p>
          </>
        ) : (
          <>
            <TextArea label="Что просим" value={what} rows={3} maxLength={1000} onChange={(e) => setWhat(e.target.value)}
              hint="Клиент увидит это на странице: например, «доступ к серверу 1С и к хостингу сайта». Без паролей." />
            <p className="text-meta text-p-text-muted">
              Клиент откроет ссылку и впишет доступы на странице таскера — они сразу лягут в хранилище секретов, не проходя через чат. Отправь ссылку как удобно: в переписку, почтой, мессенджером.
            </p>
          </>
        )}
      </div>
    </Sheet>
  );
}
